import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageLayout from "../../components/layout/PageLayout";
import {
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
} from "../../store/api";
import { extractList } from "../../utils/response";
import { useLazySelectOptions } from "../../hooks/useLazySelectOptions";
import { useServerValidationErrors } from "../../hooks/useServerValidationErrors";
import { toast } from "react-toastify";
import {
  useCreateServicePlanMutation,
  useGetServicePlanByIdQuery,
  useUpdateServicePlanMutation,
} from "../../store/api/servicePlansApi";
import { SERVICE_PLANS_LIST_PATH } from "../../navigations/path";

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append("status_id", values.status_id);
  fd.append("name", values.name ?? "");
  fd.append("price", String(values.price ?? ""));
  fd.append("description", values.description ?? "");
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const CreateServicePlan = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatuses] = useLazyGetStatusesQuery();

  const serviceStatusTrigger = useCallback(
    () => ({
      unwrap: async () => {
        try {
          return await fetchStatusesByModel("servicePlan").unwrap();
        } catch {
          return await fetchStatuses({ paginate: 0 }).unwrap();
        }
      },
    }),
    [fetchStatusesByModel, fetchStatuses],
  );

  const statusSelect = useLazySelectOptions(serviceStatusTrigger, {
    mapResult: (res) => {
      const list = extractList(res);
      const arr = Array.isArray(list) ? list : [];
      return arr.map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const loadStatuses = useCallback(
    () =>
      statusSelect.load().catch(() => {
        toast.error("Could not load statuses");
      }),
    [statusSelect],
  );

  const {
    data: servicePlanData,
    isLoading: servicePlanLoading,
    isError: servicePlanError,
    error: servicePlanErr,
  } = useGetServicePlanByIdQuery(
    { id: editId /*params: buildServiceRelationParams()*/ },
    { skip: !isEdit || !editId },
  );
  const [createServicePlan, { isLoading: creating }] =
    useCreateServicePlanMutation();
  const [updateServicePlan, { isLoading: updating }] =
    useUpdateServicePlanMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && (servicePlanLoading || !hydrated);

  useEffect(() => {
    if (servicePlanError) {
      toast.error(
        servicePlanErr?.data?.message || "Failed to load service plan",
      );
      navigate(SERVICE_PLANS_LIST_PATH);
    }
  }, [servicePlanError, servicePlanErr, navigate]);

  useEffect(() => {
    if (!isEdit || !servicePlanData || hydrated) return;
    const s = servicePlanData?.data ?? servicePlanData;
    if (!s) return;

    setName(s.name ?? "");
    setPrice(s.price != null ? String(s.price) : "");
    setDescription(s.description ?? "");

    const nestedStatus =
      s.status && typeof s.status === "object" ? s.status : null;
    const sid = s.status_id ?? nestedStatus?.id ?? undefined;

    if (sid) {
      if (nestedStatus) {
        const opt = {
          id: sid,
          label: statusLabel(nestedStatus),
          raw: nestedStatus,
        };
        setSelectedStatus(opt);
        statusSelect.setOptions((prev) =>
          prev.some((o) => o.id === sid) ? prev : [...prev, opt],
        );
      } else {
        const opt = statusSelect.options.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [isEdit, servicePlanData, statusSelect.options, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = {};
    if (!name.trim()) clientErrors.name = "Name is required";
    if (!selectedStatus?.id) clientErrors.status_id = "Status is required";
    if (price === "" || Number.isNaN(Number(price)))
      clientErrors.price = "Enter valid price";
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      price: price !== "" ? Number(price) : null,
      description: description.trim(),
    };
    const fd = buildFormData(values);

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateServicePlan({ id: editId, data: fd }).unwrap();
        toast.success("Service plan updated");
      } else {
        await createServicePlan(fd).unwrap();
        toast.success("Service plan added");
      }
      navigate(SERVICE_PLANS_LIST_PATH);
    } catch (err) {
      applyApiError(
        err,
        isEdit ? "Failed to update service plan" : "Failed to add service plan",
      );
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(SERVICE_PLANS_LIST_PATH)}
          sx={{ mb: 2 }}
        >
          Back to service plans
        </Button>
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}
        >
          {isEdit ? "Edit service plan" : "Add service plan"}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError("name");
                  }}
                  required
                  fullWidth
                  {...fieldProps("name")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Price"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    clearError("price");
                  }}
                  required
                  fullWidth
                  type="number"
                  {...fieldProps("price")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearError("description");
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  {...fieldProps("description")}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  options={statusSelect.options}
                  value={selectedStatus}
                  onChange={(_, v) => {
                    setSelectedStatus(v);
                    clearError("status_id");
                  }}
                  onOpen={loadStatuses}
                  loading={statusSelect.loading}
                  getOptionLabel={(opt) =>
                    opt?.label != null ? opt.label : ""
                  }
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      fullWidth
                      {...fieldProps("status_id")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    "Save changes"
                  ) : (
                    "Add service plan"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(SERVICE_PLANS_LIST_PATH)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateServicePlan;
